import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardShortcut } from '../lib/keyboard';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  commands: KeyboardShortcut[];
}

/**
 * Spotlight / Linear / VSCode-style command palette.
 *
 * Triggered by Cmd-K (macOS) or Ctrl-K (Windows / Linux). The keyboard
 * shortcut is wired in Layout so it works even while focus is inside a
 * form field — that's the whole point of a quick switcher.
 *
 * The list is the same one the help dialog uses, so what you can press
 * from anywhere (g h, g c, ?) is exactly what you can find here.
 */
export function CommandPalette({ isOpen, onClose, commands }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Filter — case-insensitive substring match against label, group, combo.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.group.toLowerCase().includes(q) ||
        c.combo.toLowerCase().includes(q),
    );
  }, [commands, query]);

  // Reset state every time the palette opens; capture the trigger element
  // so we can restore focus on close.
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    setQuery('');
    setActiveIndex(0);
    // requestAnimationFrame so the input is in the DOM before .focus() fires.
    const raf = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(raf);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isOpen]);

  // Clamp activeIndex when the filtered list shrinks.
  useEffect(() => {
    if (activeIndex > filtered.length - 1) {
      setActiveIndex(Math.max(0, filtered.length - 1));
    }
  }, [activeIndex, filtered.length]);

  // Keep the active row scrolled into view as the user arrows through.
  useEffect(() => {
    if (!isOpen) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (filtered.length === 0 ? 0 : (i + 1) % filtered.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) =>
        filtered.length === 0 ? 0 : (i - 1 + filtered.length) % filtered.length,
      );
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[activeIndex];
      if (cmd) {
        onClose();
        try {
          cmd.action();
        } catch (err) {
          // Without this the palette closed and the user got nothing —
          // no toast, no console line. Surface it.
          // eslint-disable-next-line no-console
          console.error('CommandPalette action failed:', cmd.label, err);
        }
      }
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-[var(--z-modal)] flex items-start justify-center px-4 pt-[12vh]"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close command palette"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[var(--color-navy)]/40 backdrop-blur-[2px]"
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-[560px] overflow-hidden rounded-[10px] border border-[var(--color-rule-strong)] bg-[var(--color-surface)] shadow-[0_24px_48px_-16px_rgba(25,54,97,0.32)] prism-enter">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-[var(--color-rule)] px-4 py-3">
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-[var(--color-ink-soft)]"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command or search…"
            aria-label="Command query"
            aria-controls="command-palette-list"
            aria-activedescendant={
              filtered[activeIndex] ? `cmd-${activeIndex}` : undefined
            }
            className="w-full border-0 bg-transparent font-sans text-[15px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-soft)] focus:outline-none"
          />
          <kbd className="hidden rounded-[4px] border border-[var(--color-rule-strong)] bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[10.5px] font-semibold text-[var(--color-ink-muted)] sm:inline-block">
            esc
          </kbd>
        </div>

        {/* Results */}
        <ul
          id="command-palette-list"
          ref={listRef}
          role="listbox"
          aria-label="Available commands"
          className="max-h-[50vh] overflow-y-auto py-2"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-6 text-center font-sans text-[13px] text-[var(--color-ink-soft)]">
              No commands match “{query}”.
            </li>
          ) : (
            filtered.map((cmd, i) => {
              const isActive = i === activeIndex;
              return (
                <li
                  key={cmd.combo + cmd.label}
                  id={`cmd-${i}`}
                  role="option"
                  aria-selected={isActive}
                  data-index={i}
                  onMouseMove={() => setActiveIndex(i)}
                  onClick={() => {
                    onClose();
                    cmd.action();
                  }}
                  className={[
                    'flex cursor-pointer items-center gap-3 px-4 py-2 font-sans text-[13.5px]',
                    isActive
                      ? 'bg-[var(--color-primary)]/10 text-[var(--color-ink)]'
                      : 'text-[var(--color-ink)]',
                  ].join(' ')}
                >
                  <span
                    aria-hidden="true"
                    className={[
                      'h-[6px] w-[6px] shrink-0 rounded-full',
                      isActive
                        ? 'bg-[var(--color-primary)]'
                        : 'bg-[var(--color-rule-strong)]',
                    ].join(' ')}
                  />
                  <span className="flex-1">{cmd.label}</span>
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--color-ink-soft)]">
                    {cmd.group}
                  </span>
                  <ShortcutCombo combo={cmd.combo} />
                </li>
              );
            })
          )}
        </ul>

        <div className="border-t border-[var(--color-rule)] bg-[var(--color-bg)] px-4 py-2 font-sans text-[11px] text-[var(--color-ink-soft)]">
          <kbd className="rounded-[3px] border border-[var(--color-rule-strong)] bg-[var(--color-surface)] px-1 font-mono text-[10px] text-[var(--color-ink-muted)]">
            ↑↓
          </kbd>{' '}
          to navigate ·{' '}
          <kbd className="rounded-[3px] border border-[var(--color-rule-strong)] bg-[var(--color-surface)] px-1 font-mono text-[10px] text-[var(--color-ink-muted)]">
            ↵
          </kbd>{' '}
          to run ·{' '}
          <kbd className="rounded-[3px] border border-[var(--color-rule-strong)] bg-[var(--color-surface)] px-1 font-mono text-[10px] text-[var(--color-ink-muted)]">
            esc
          </kbd>{' '}
          to close
        </div>
      </div>
    </div>
  );
}

function ShortcutCombo({ combo }: { combo: string }) {
  const keys = combo.split(' ');
  return (
    <span className="flex items-center gap-1">
      {keys.map((k, i) => (
        <kbd
          key={`${k}-${i}`}
          className="rounded-[3px] border border-[var(--color-rule-strong)] bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[10.5px] font-semibold text-[var(--color-ink-muted)]"
        >
          {k}
        </kbd>
      ))}
    </span>
  );
}
